import * as React from "react";
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from "@nosible/shared/lib/utils";

const cardVariants = cva(
  'flex flex-col gap-6 py-6 shadow-sm relative overflow-hidden',
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground border rounded-xl',
        primary: 'bg-primary text-primary-foreground',
        secondary: 'bg-secondary text-secondary-foreground',
      },
      notch: {
        none: 'rounded-xl',
        'top-left': '[clip-path:polygon(var(--notch-size)_0,100%_0,100%_100%,0_100%,0_var(--notch-size))]',
        'top-right': '[clip-path:polygon(0_0,calc(100%_-_var(--notch-size))_0,100%_var(--notch-size),100%_100%,0_100%)]',
        'bottom-left': '[clip-path:polygon(0_0,100%_0,100%_100%,var(--notch-size)_100%,0_calc(100%_-_var(--notch-size)))]',
        'bottom-right': '[clip-path:polygon(0_0,100%_0,100%_calc(100%_-_var(--notch-size)),calc(100%_-_var(--notch-size))_100%,0_100%)]',
      },
    },
    defaultVariants: {
      variant: 'default',
      notch: 'none',
    },
  }
);

interface CardProps extends React.ComponentProps<"div">, VariantProps<typeof cardVariants> {
  notchSize?: number;
}

function Card({ className, variant, notch, notchSize = 20, ...props }: CardProps) {
  return (
    <div
      data-slot="card"
      className={cn(cardVariants({ variant, notch }), className)}
      style={{
        '--notch-size': `${notchSize}px`,
      } as React.CSSProperties}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-title" className={cn("leading-none font-semibold font-display", className)} {...props} />;
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-description" className={cn("text-muted-foreground text-sm", className)} {...props} />;
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn("col-start-2 row-span-2 row-start-1 self-start justify-self-end", className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-content" className={cn("px-6", className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="card-footer" className={cn("flex items-center px-6 [.border-t]:pt-6", className)} {...props} />
  );
}

export { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle };
